import { useEffect, useState } from 'react';

import { useCommands } from './useCommands';
import { useParamsQuery, usePricingQuery } from './useQueries';

import type { CoverageKey, PropertyMultiRiskKey } from '../models/coverage';
import type { CoverageValues } from '@insify/funnel-components';

export const useCoverageValues = (): CoverageValues<CoverageKey, PropertyMultiRiskKey> & {
  setIsLoading: (isLoading: boolean) => void;
} => {
  const [isLoading, setIsLoading] = useState(false);
  const {
    data: { deductible, professionalLiability, publicLiability, carInsurance } = {},
    isFetching: isFetchingParams,
  } = useParamsQuery();
  const {
    data: { totalPayable, premiumBeforeDiscount, startersDiscount, tax, fees } = {},
    isFetching: isFetchingPricing,
    isError,
  } = usePricingQuery();
  const enabledPublicLiability = Number(publicLiability?.coverLimit?.cents) > 0;
  const { noPendingCommands } = useCommands();

  useEffect(() => {
    setIsLoading(!noPendingCommands || isFetchingPricing || isFetchingParams);
  }, [noPendingCommands, isFetchingPricing, isFetchingParams]);

  useEffect(() => {
    if (isError) {
      // Not the most pretty UI but 1. it hardly happens 2. it's better than nothing
      window.alert('An error occured while calculating the price. Please try again later.');
    }
  }, [isError]);

  return {
    params: {
      deductible,
      professionalLiability: professionalLiability?.coverLimit,
      publicLiability: enabledPublicLiability,
      carInsurance: carInsurance?.interested,
    },
    fees,
    premiums: {
      total: {
        amount: totalPayable,
        amountTax: tax,
        amountBeforeDiscount: premiumBeforeDiscount,
        amountDiscount: startersDiscount,
      },
      coverages: [
        {
          // This coverage is used for both professionalLiability + publicLiability
          type: 'professionalLiability',
          amount: totalPayable,
        },
      ],
    },
    isLoading,
    setIsLoading,
  };
};
